/**
 * Conversation configurations for the Spitex vertical.
 *
 * Two agents: supply-side (caregiver recruitment) and demand-side (patient/family).
 * Both operate under DSG/healthcare compliance.
 *
 * IMPORTANT: Do NOT collect or process health data in messages.
 * Consent jurisdiction: ch-dsg-health (from verticalConfig.compliance.jurisdiction).
 *
 * Prompt length: ChatSuite validates system_prompt at max 32000 chars
 * (apps/api/src/routes/ai-agents.ts:73). A comment here previously claimed a
 * 5000-char cap, which is wrong and made contributors trim compliance copy
 * unnecessarily: live prompts run 4.4k-10.2k today. Verify against the route
 * schema, not this comment, before assuming a limit. Keep fields, rules
 * and contextBlocks compact.
 */

import type { ConversationConfig } from '@selenos/leads/conversation';

/**
 * Supply-side: qualifies caregivers for Spitex positions.
 *
 * Two paths:
 * - Angehoerigenpflege: family members already caring (no quals needed)
 * - Professional: nurses/carers seeking Spitex employment
 *
 * Routing field: customFields.serviceInterest
 */
export const spitexSupplyConversationConfig: ConversationConfig = {
  // IDENTITY (Commerce Policy s.1): name ourselves in the first line. This
  // greeting previously named nobody, so a caregiver had no idea who had
  // messaged them -- the "surprise" s.1 forbids, and the same shape that got
  // the drivers number restricted. Note the deliberate TWO-party split kept
  // throughout this vertical: IhrSpitex is US (who messages, and whose agent
  // this is); Spitex Frei is the EMPLOYER we forward accepted leads to (who
  // actually calls). Both must stay named and distinct: naming only Spitex
  // Frei would misrepresent who is contacting them, and collapsing them would
  // break the consent disclosure at `consentPrompt`.
  greeting:
    'Gruezi, hier ist IhrSpitex. Pflegen Sie einen Angehoerigen oder suchen Sie eine Stelle in der Spitex?',
  tone: 'WhatsApp-Agent fuer IhrSpitex (Schweiz). Warm, empathisch, wertschaetzend. Viele Schreibende pflegen bereits Angehoerige. Kurz und klar, max 160 Zeichen. Sie-Form (Du ok, wenn Person zuerst duzt). Nie erwaehnen, dass Sie KI sind.',
  languageMatching: true,
  supportedLanguages: ['de', 'fr', 'it', 'en'],
  defaultLanguage: 'de',

  fields: [
    {
      // ROUTING FIELD — MUST be first. getOutstandingFields returns blocking
      // fields in this array order, so the server's nextField on an empty
      // conversation is whatever sits at fields[0]. The skipWhen guards on the
      // path-specific fields below all key off serviceInterest, so until it is
      // answered NONE of them can prune. Asking it first is what collapses the
      // field set to the short Angehoerigenpflege path early — instead of
      // facing the full 10-field superset, a family caregiver who answers
      // "angehoerigenpflege" sees the professional-path fields (qualifications,
      // experienceYears) pruned immediately. This is the "Situation" step of
      // the in-WhatsApp wage quiz (P1-B); it routes onto the supply routing
      // field. firstName/lastName follow it (the agent still has the name early
      // enough to address the person by name for the rest of the conversation).
      key: 'customFields.serviceInterest',
      question:
        'Pflegen Sie bereits einen Angehoerigen zu Hause oder suchen Sie eine Stelle als Pflegefachperson?',
      type: 'enum',
      enumValues: ['angehoerigenpflege', 'betreuung', 'hauswirtschaft'],
      priority: 'required',
      clarificationHint:
        'angehoerigenpflege=pflegen Angehoerigen (Lohn moeglich); betreuung=Pflegestelle; hauswirtschaft=Haushalt/Putzen',
    },
    // Name right after routing, so the agent has it early and can address the
    // person by name throughout the rest of the conversation.
    {
      key: 'firstName',
      question: 'Wie heissen Sie? (Vorname)',
      type: 'string',
      priority: 'required',
    },
    {
      key: 'lastName',
      question: 'Und Ihr Nachname?',
      type: 'string',
      priority: 'required',
    },

    // --- Angehoerigenpflege wage-quiz path (P1-B) ---
    // Ordered as the wage quiz needs it (wage-quiz-flow.ts is the source of
    // truth): relationship -> care tasks -> daily hours -> canton -> [reveal]
    // -> krankenkasse -> medizinische_ausbildung. The agent calls
    // estimate_caregiver_wage once tasks + hours + canton are collected (canton
    // is the last of the three so the reveal fires right after it), states the
    // figure as a Schaetzung, then continues to krankenkasse + consent + handoff.
    {
      key: 'customFields.careRelationship',
      question: 'Wen pflegen Sie? (z.B. Mutter, Partner/in)',
      type: 'string',
      priority: 'required',
      skipWhen: {
        field: 'customFields.serviceInterest',
        operator: 'neq',
        value: 'angehoerigenpflege',
      },
    },
    {
      key: 'customFields.careActivities',
      question: 'Was machen Sie? (z.B. Waschen, Ankleiden, Hautpflege)',
      type: 'string',
      // Required on the Angehoerigenpflege path: it is one of the three
      // estimate_caregiver_wage inputs (tasks). Without it the agent cannot run
      // the wage reveal, so it must be collected before canton.
      priority: 'required',
      skipWhen: {
        field: 'customFields.serviceInterest',
        operator: 'neq',
        value: 'angehoerigenpflege',
      },
    },
    {
      key: 'customFields.careHoursPerWeek',
      question: 'Wie viele Stunden pro Woche pflegen Sie?',
      type: 'number',
      // Required on the Angehoerigenpflege path: the second estimate_caregiver_
      // wage input (hours). The agent divides by 7 to get hours/day for the
      // estimate. Collected before canton.
      priority: 'required',
      skipWhen: {
        field: 'customFields.serviceInterest',
        operator: 'neq',
        value: 'angehoerigenpflege',
      },
    },
    {
      // Canton is the LAST of the three estimate_caregiver_wage inputs, so the
      // agent runs the wage reveal immediately after it is answered. Required on
      // every path (footprint gate); disqualifies an unsupported canton.
      key: 'customFields.canton',
      question: 'In welchem Kanton wohnen Sie?',
      type: 'enum',
      enumValues: ['ZH', 'AG', 'BE', 'ZG', 'SG', 'SZ', 'LU'],
      priority: 'required',
      clarificationHint: 'Wir sind in ZH, AG, BE, ZG, SG, SZ, LU taetig.',
      disqualifyWhen: {
        operator: 'neq',
        value: ['ZH', 'AG', 'BE', 'ZG', 'SG', 'SZ', 'LU'],
        message:
          'Vielen Dank. Leider sind wir in Ihrem Kanton noch nicht taetig.',
      },
    },
    {
      // Krankenkasse = the cared-for person's health insurer. REQUIRED per
      // operator Osman (2026-06-06): Spitex Frei cannot bill an Angehoerigen-
      // pflege placement without it, so a lead on this path is NOT forwardable
      // until it is collected. Gates `mayComplete` (the point the supply Slack
      // alert fires). Applicable only on the Angehoerigenpflege path; for the
      // job-seeker paths there is no patient, so it is skipped (never blocks).
      key: 'customFields.krankenkasse',
      question: 'Bei welcher Krankenkasse ist die gepflegte Person versichert?',
      type: 'string',
      priority: 'required',
      skipWhen: {
        field: 'customFields.serviceInterest',
        operator: 'neq',
        value: 'angehoerigenpflege',
      },
      clarificationHint: 'z.B. CSS, Helsana, Swica, Sanitas, KPT, Concordia.',
    },
    {
      // Medizinische Ausbildung = does the caregiver have medical training?
      // CAPTURED, never a disqualifier (operator Osman, 2026-06-06): Spitex Frei
      // employs untrained caregivers and covers the mandatory Pflegehelferkurs
      // post-hire. `optional` so it NEVER gates mayComplete; the agent only asks
      // if it fits the conversation.
      key: 'customFields.medizinischeAusbildung',
      question: 'Haben Sie eine medizinische oder pflegerische Ausbildung?',
      type: 'enum',
      enumValues: ['ja_abgeschlossen', 'in_ausbildung', 'nein'],
      priority: 'optional',
      clarificationHint:
        'Keine Ausbildung ist kein Hindernis: Spitex Frei uebernimmt den Pflegehelferkurs.',
      skipWhen: {
        field: 'customFields.serviceInterest',
        operator: 'neq',
        value: 'angehoerigenpflege',
      },
    },
    // --- Professional path ---
    {
      key: 'customFields.qualifications',
      question: 'Welche Ausbildung haben Sie?',
      type: 'enum',
      enumValues: ['hf_pflege', 'fh_pflege', 'fage', 'fabe', 'ags', 'other'],
      // Important, not required: caregivers (Angehoerigenpflege) need no
      // qualification, and even professionals may not state one — the agent
      // asks but the conversation never blocks on it.
      priority: 'important',
      clarificationHint: 'HF/FH Pflege, FaGe, FaBe, AGS, andere',
      skipWhen: {
        field: 'customFields.serviceInterest',
        operator: 'eq',
        value: 'angehoerigenpflege',
      },
    },
    {
      key: 'customFields.experienceYears',
      question: 'Wie viele Jahre Erfahrung in der Pflege?',
      type: 'number',
      priority: 'important',
      skipWhen: {
        field: 'customFields.serviceInterest',
        operator: 'eq',
        value: 'angehoerigenpflege',
      },
    },

    // --- Shared (both paths) ---
    // Contact + locality, collected for the human follow-up. `important` so they
    // never block the wage reveal or `mayComplete`: the agent asks them (the
    // user may decline email), but capture + handoff is the goal, not an in-chat
    // close. On the Angehoerigenpflege path these come after the wage reveal so
    // the anchored figure has already earned the contact details.
    {
      key: 'email',
      question: 'Wie lautet Ihre E-Mail-Adresse?',
      type: 'string',
      priority: 'important',
    },
    {
      key: 'customFields.city',
      question: 'In welcher Ortschaft wohnen Sie?',
      type: 'string',
      priority: 'important',
    },
    {
      key: 'customFields.availability',
      question: 'Welches Pensum bevorzugen Sie?',
      type: 'enum',
      enumValues: [
        'full_time',
        'part_time',
        'on_call',
        'weekends_only',
        'flexible',
      ],
      priority: 'important',
    },
    {
      key: 'customFields.languages',
      question: 'Welche Sprachen sprechen Sie? (Mehrfachauswahl)',
      type: 'array',
      enumValues: ['de', 'fr', 'it', 'en', 'pt', 'es', 'sq', 'sr', 'tr'],
      priority: 'important',
    },
  ],

  contextBlocks: [
    {
      label: 'Kontext',
      content:
        'Angehoerigenpflege: pflegendes Familienmitglied kann angestellt werden, CHF 35-40/h, keine Ausbildung noetig, ueber KVG abgerechnet. Hauswirtschaft: CHF 28-30/h fuer Angehoerige. Behandlungspflege (Spezialitaet): Infusionen/Spritzen zu Hause - das bieten die meisten Spitex nicht.',
    },
  ],

  consentPrompt:
    'Duerfen wir Ihre Angaben an Spitex Frei weiterleiten und Sie bezueglich der Stelle kontaktieren?',
  completionMessage:
    'Vielen Dank, {{firstName}}! Spitex Frei meldet sich innerhalb von 24 Stunden.',
  // Catch-all close for closeStyle "not_proceeding". Reason-free on purpose: the
  // unsupported-canton case already has its own worded rule on the `canton`
  // field, so naming the region HERE told every other disqualification (a
  // scoring outcome the agent cannot see) a reason that may be false.
  disqualificationMessage:
    'Vielen Dank fuer Ihre Zeit, {{firstName}}. Wir koennen im Moment leider nicht weitermachen. Ihre Angaben bleiben bei uns, melden Sie sich hier jederzeit wieder.',

  rules: [
    'Folgen Sie immer dem nextQuestion des Tools: zuerst die Situation (Angehoerigenpflege vs Stelle), dann Vor-/Nachname. Sprechen Sie die Person ab dem Vornamen damit an.',
    'Eine Frage pro Nachricht, max 160 Zeichen.',
    'Bei Angehoerigenpflege: positiv reagieren (Lohn moeglich, keine Quals).',
    'Keine Qualifikationsfragen bei serviceInterest=angehoerigenpflege.',
    'Bei Angehoerigenpflege nach Taetigkeiten + Pflegestunden + Kanton: estimate_caregiver_wage aufrufen, dann den Betrag als unverbindliche Schaetzung nennen (mit Hinweis "keine Lohnzusage"). Nie eine Zahl erfinden.',
    'Nach der Schaetzung weiter zu Krankenkasse + Einverständnis fragen. Ziel ist die Uebergabe an eine Koordinatorin, NICHT der Abschluss im Chat.',
    'Zu Lohn: "CHF 35-40/h bei Angehoerigenpflege, Details persoenlich."',
    'Arbeitgeber: Spitex Frei (anerkannte private Spitex in 7 Kantonen).',
    'KEINE Gesundheitsdaten von Patienten erfragen.',
    'Keine E-Mail? Als declined markieren (declinedFields) und weiter — nicht erneut fragen.',
  ],
};

/**
 * Demand-side: qualifies families seeking home care services.
 * Runs as a widget chatbot on ihrspitex.ch (NOT WhatsApp).
 */
export const spitexDemandConversationConfig: ConversationConfig = {
  tone: 'Chatbot-Empfangsagent auf ihrspitex.ch. Zielgruppe: Familien/Patienten mit Pflegebedarf in der Schweiz. Einfuehlsam, ruhig, professionell (wie erfahrene Arztpraxis-Koordinatorin). Max 160 Zeichen. Immer "Sie". Kein Fachjargon. Nie erwaehnen, dass Sie KI sind. Wenn Familie bereits pflegt: aktiv auf Angehoerigenpflege-Lohn hinweisen.',
  languageMatching: true,
  supportedLanguages: ['de', 'fr', 'it'],
  defaultLanguage: 'de',

  greeting:
    'Willkommen bei IhrSpitex. Wir bieten Pflege zu Hause, inkl. Infusionen/Spritzen. Fuer wen suchen Sie Unterstuetzung?',

  fields: [
    {
      key: 'customFields.serviceType',
      question: 'Welche Art von Hilfe benoetigen Sie?',
      type: 'enum',
      enumValues: [
        'behandlungspflege',
        'angehoerigenpflege',
        'betreuung',
        'hauswirtschaft',
      ],
      priority: 'required',
      clarificationHint:
        'behandlungspflege=Infusionen/Spritzen/Wunden; angehoerigenpflege=pflege selbst, will Lohn; betreuung=Fachpflege zu Hause; hauswirtschaft=Putzen/Kochen',
    },
    {
      key: 'customFields.familyAlreadyCaring',
      question:
        'Pflegen Sie oder ein Familienmitglied die betroffene Person bereits?',
      type: 'boolean',
      priority: 'required',
      skipWhen: {
        field: 'customFields.serviceType',
        operator: 'eq',
        value: 'angehoerigenpflege',
      },
      clarificationHint:
        'Bei ja: Lohn CHF 35-40/h moeglich, keine Qualifikation noetig.',
    },
    {
      key: 'customFields.canton',
      question: 'In welchem Kanton benoetigen Sie Pflege?',
      type: 'string',
      priority: 'required',
      clarificationHint: 'z.B. ZH, BE, AG, LU, SG, SZ, ZG',
      disqualifyWhen: {
        operator: 'neq',
        value: ['ZH', 'AG', 'BE', 'ZG', 'SG', 'SZ', 'LU'],
        message:
          'Danke. Wir sind nur in ZH, AG, BE, ZG, SG, SZ, LU taetig. Fuer andere Regionen: spitex.ch.',
      },
    },
    {
      key: 'firstName',
      question: 'Wie heissen Sie? (Vorname)',
      type: 'string',
      priority: 'required',
    },
    {
      key: 'lastName',
      question: 'Und Ihr Nachname?',
      type: 'string',
      priority: 'required',
    },
    {
      key: 'customFields.urgency',
      question: 'Wann wird die Pflege benoetigt?',
      type: 'enum',
      enumValues: [
        'sofort',
        'innerhalb_1_woche',
        'innerhalb_1_monat',
        'spaeter',
      ],
      priority: 'important',
    },
    {
      key: 'customFields.careDescription',
      question: 'Kurz: Welche Hilfe wird benoetigt?',
      type: 'string',
      priority: 'important',
      clarificationHint:
        'z.B. "Mutter braucht Hilfe beim Anziehen" oder "Uebergangspflege nach Spital"',
    },
    {
      key: 'email',
      question: 'Ihre E-Mail-Adresse (optional)?',
      type: 'string',
      priority: 'optional',
    },
  ],

  contextBlocks: [
    {
      label: 'Kosten und Leistungen',
      content:
        'Angehoerigenpflege (einzigartig): Familienmitglied wird angestellt, CHF 35-40/h, keine Quals. Patient CHF 7.65-15.35/Tag (KVG deckt Rest). Behandlungspflege (Spezialitaet): taeglich Infusionen/Spritzen/Wunden - andere Spitex bieten das meist NICHT. Patient CHF 7.65-15.35/Tag. Hauswirtschaft: CHF 48/h Selbstzahler; Angehoerige erhalten CHF 28-30/h. Notfall: Sanitaet 144.',
    },
  ],

  consentPrompt:
    'Duerfen wir Sie bezueglich Spitex-Pflege kontaktieren? Daten gemaess nDSG verarbeitet.',

  completionMessage:
    'Vielen Dank, {{firstName}}. Eine Koordinatorin meldet sich innerhalb von 1 Stunde bei Ihnen.',

  // Same reason-free rule as the supply config: the canton list lives on the
  // `canton` field's own disqualifyWhen message. The spitex.ch pointer stays
  // because a family looking for care should not be left with nowhere to go, and
  // a national directory is not a reason for the outcome.
  disqualificationMessage:
    'Vielen Dank fuer Ihre Zeit, {{firstName}}. Wir koennen Ihnen im Moment leider nicht weiterhelfen. Eine Uebersicht aller Anbieter finden Sie auf spitex.ch.',

  rules: [
    'KEINE medizinischen Diagnosen erfragen. Nur Pflegesituation.',
    'Bei Spritzen/Infusionen: "Unsere Spezialitaet, taeglich moeglich."',
    'Eine Frage pro Nachricht, max 160 Zeichen.',
    'Bei erwaehnter Angehoerigenpflege: SOFORT auf Lohn (CHF 35-40/h, keine Quals) hinweisen.',
    'Notfall: auf 144 verweisen, keine weitere Beratung.',
    'Emotionen: kurz validieren, dann ruhig weiter.',
    'Anfrage fuer andere: Kontaktdaten der Anfragenden erfassen.',
    'Kanton ausserhalb 7 Kantonen: auf spitex.ch verweisen.',
    'Widget auf ihrspitex.ch, keine WhatsApp-Hinweise.',
  ],
};
